import {
	customerPortalActorTable,
	customerPortalUserProjectTable,
	linearProjectMilestoneTable,
} from "@mojo/db/schema";
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import { eq, inArray } from "drizzle-orm";
import { auth } from "@/lib/auth";
import { db } from "@/lib/db";

// Types
export type Actor = {
	id: string;
	name: string;
	color: string;
};

export type Epic = {
	id: string;
	name: string;
	projectId: string;
};

// Helper to get user's project IDs
async function getUserProjectIds(userId: string): Promise<string[]> {
	const userProjects = await db
		.select({ projectId: customerPortalUserProjectTable.projectId })
		.from(customerPortalUserProjectTable)
		.where(eq(customerPortalUserProjectTable.userId, userId));
	return userProjects.map((p) => p.projectId);
}

// ============ ACTORS ============

// Get all portal actors
export const getActors = createServerFn({ method: "GET" }).handler(
	async (): Promise<Actor[]> => {
		const actors = await db
			.select({
				id: customerPortalActorTable.id,
				name: customerPortalActorTable.name,
				color: customerPortalActorTable.color,
			})
			.from(customerPortalActorTable);

		return actors;
	},
);

// Create a new actor
export const createActor = createServerFn({ method: "POST" })
	.inputValidator((data: { name: string; color: string }) => data)
	.handler(async ({ data }): Promise<Actor> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			throw new Error("Unauthorized");
		}

		const [actor] = await db
			.insert(customerPortalActorTable)
			.values({
				name: data.name,
				color: data.color,
			})
			.returning({
				id: customerPortalActorTable.id,
				name: customerPortalActorTable.name,
				color: customerPortalActorTable.color,
			});

		return actor;
	});

// Update an actor
export const updateActor = createServerFn({ method: "POST" })
	.inputValidator((data: { id: string; name: string; color: string }) => data)
	.handler(async ({ data }): Promise<Actor> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			throw new Error("Unauthorized");
		}

		const [actor] = await db
			.update(customerPortalActorTable)
			.set({
				name: data.name,
				color: data.color,
			})
			.where(eq(customerPortalActorTable.id, data.id))
			.returning({
				id: customerPortalActorTable.id,
				name: customerPortalActorTable.name,
				color: customerPortalActorTable.color,
			});

		return actor;
	});

// Delete an actor
export const deleteActor = createServerFn({ method: "POST" })
	.inputValidator((data: { id: string }) => data)
	.handler(async ({ data }): Promise<void> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			throw new Error("Unauthorized");
		}

		await db
			.delete(customerPortalActorTable)
			.where(eq(customerPortalActorTable.id, data.id));
	});

// ============ EPICS (Milestones) ============

// Get all epics for user's projects
export const getEpics = createServerFn({ method: "GET" }).handler(
	async (): Promise<Epic[]> => {
		const headers = getRequestHeaders();
		const session = await auth.api.getSession({ headers });

		if (!session?.user?.id) {
			return [];
		}

		const projectIds = await getUserProjectIds(session.user.id);

		if (projectIds.length === 0) {
			return [];
		}

		const epics = await db
			.select({
				id: linearProjectMilestoneTable.id,
				name: linearProjectMilestoneTable.name,
				projectId: linearProjectMilestoneTable.projectId,
			})
			.from(linearProjectMilestoneTable)
			.where(inArray(linearProjectMilestoneTable.projectId, projectIds))
			.orderBy(linearProjectMilestoneTable.sortOrder);

		return epics;
	},
);

// Note: Epics (milestones) are read-only as they are synced from Linear.
// CRUD operations for epics should be done in Linear directly.
